import type {
  tWapiDisconnection,
  tWapiDisconnectionCase,
  tWapiDisconnectionInput,
  tWapiDisconnectionResolution,
} from './WapiDisconnection.types';

const MINUTE_MS = 60 * 1000;

/**
 * Cases 4 and 5 appear 5 minutes after the disconnection: a phone that was
 * only briefly offline often comes back by itself, and an alarm that clears
 * itself a minute later teaches the customer to ignore it.
 */
export const WAPI_INACTIVITY_GRACE_MS = 5 * MINUTE_MS;

/** 8a ("se está reconectando") turns into 8b ("termina el cambio") here. */
export const WAPI_OFFBOARDED_PATIENCE_MS = 10 * MINUTE_MS;

/**
 * INT-1653 ajuste 3 (19-oct) — past a day out, the number is not "about to
 * come back": 8b turns into 8d ("sigue sin conectarse"). WBS keeps sending
 * `offboardedAt` after 24 h for this (INT-1676).
 */
export const WAPI_OFFBOARDED_STALE_MS = 24 * 60 * MINUTE_MS;

/**
 * How long a reconnection outcome is news. whatsapp-business-service never
 * clears it (it describes the LAST reconnection), so without an age limit the
 * 8c notice would greet the workspace forever.
 */
export const WAPI_RECONNECTION_NEWS_MS = 24 * 60 * MINUTE_MS;

/** Meta's documented causes, mapped to Producto's case numbers. */
const META_REASON_CASE: Record<string, tWapiDisconnectionCase> = {
  USER_RE_REGISTERED: '1',
  CHANGE_NUMBER: '2',
  BUSINESS_DOWNGRADE: '3',
  COMPANION_INACTIVITY: '4',
  PRIMARY_INACTIVITY: '5',
  /*
   * Case 10 has no signal yet: whatsapp-business-service does not process
   * `partner_app_uninstalled` today. Read here under the name the webhook
   * carries, so the notice switches on by itself if the backend forwards it
   * as the cause; until then it never matches.
   */
  PARTNER_APP_UNINSTALLED: '10',
};

const INACTIVITY_CASES: tWapiDisconnectionCase[] = ['4', '5'];

/** The cases a closed card stays closed for, keyed by their episode. */
export const CLOSABLE_WAPI_CASES: tWapiDisconnectionCase[] = [
  '8c_same',
  '8c_other',
];

function toEpochMs(value: string | undefined): number | null {
  if (!value) return null;
  const parsed = Date.parse(value);
  return Number.isNaN(parsed) ? null : parsed;
}

function normalize(value: string | undefined): string | null {
  const trimmed = value?.trim();
  return trimmed ? trimmed.toUpperCase() : null;
}

/**
 * The case of a RETIRED account (a DISCONNECTED row). Meta's cause first; with
 * no cause, a Cloud API account had Leadsales' permissions removed (9) and
 * anything else gets the default notice (0). A cause this app does not know
 * also gets 0: Meta publishes new causes over time, and an unknown one must
 * never break the screen or borrow another cause's instructions.
 */
export function resolveRetiredCase(
  input: Pick<
    tWapiDisconnectionInput,
    'metaReason' | 'metaInitiatedBy' | 'connectionMode' | 'reason'
  >,
): tWapiDisconnectionCase {
  const reason = normalize(input.metaReason);
  if (!reason) {
    // Ajuste 8 (19-oct): only a retirement BY META means Leadsales lost its
    // permissions. A Cloud API account retired by support, by the customer or
    // by a plan change gets the default notice.
    const isCloudApiRemovedByMeta =
      normalize(input.connectionMode) === 'CLOUD_API' &&
      normalize(input.reason) === 'REMOVED_BY_META';
    return isCloudApiRemovedByMeta ? '9' : '0';
  }
  if (reason === 'ACCOUNT_DISCONNECTED') {
    const initiatedBy = normalize(input.metaInitiatedBy);
    if (initiatedBy === 'USER') return '6';
    if (initiatedBy === 'SYSTEM') return '7';
    return '0';
  }
  return META_REASON_CASE[reason] ?? '0';
}

function buildDisconnection(
  input: tWapiDisconnectionInput,
  options: { case: tWapiDisconnectionCase; occurrence?: string | null },
): tWapiDisconnection {
  return {
    case: options.case,
    metaReason: input.metaReason?.trim() || null,
    metaInitiatedBy: input.metaInitiatedBy?.trim() || null,
    occurrence: options.occurrence ?? null,
  };
}

/**
 * INT-1653 — which disconnection notice, if any, the WhatsApp API row calls
 * for right now, and when that answer next changes on its own.
 *
 * - A DISCONNECTED row is a retired account: cases 0 to 7, 9 and 10. The two
 *   inactivity cases wait `WAPI_INACTIVITY_GRACE_MS` from `disconnectedAt`
 *   (shown at once when the time is unknown).
 * - A live row with `offboardedAt` is a number Meta is moving: 8a for the
 *   first 10 minutes, 8b after, 8d after a day.
 * - A CONNECTED row with a recent `reconnectionOutcome` is 8c (see
 *   `resolveReconnectionCase`), for `WAPI_RECONNECTION_NEWS_MS` after its `at`.
 *
 * Pure: the clock comes in as `now`, so the provider decides when to ask
 * again (`recheckAt`).
 */
export function resolveWapiDisconnection(
  input: tWapiDisconnectionInput,
): tWapiDisconnectionResolution {
  const { now } = input;

  if (input.state === 'DISCONNECTED') {
    const retiredCase = resolveRetiredCase(input);
    const disconnectedAt = toEpochMs(input.disconnectedAt);
    if (INACTIVITY_CASES.includes(retiredCase) && disconnectedAt !== null) {
      const showAt = disconnectedAt + WAPI_INACTIVITY_GRACE_MS;
      if (now < showAt) return { disconnection: null, recheckAt: showAt };
    }
    return {
      disconnection: buildDisconnection(input, { case: retiredCase }),
      recheckAt: null,
    };
  }

  const offboardedAt = toEpochMs(input.offboardedAt);
  if (offboardedAt !== null) {
    const patienceEnds = offboardedAt + WAPI_OFFBOARDED_PATIENCE_MS;
    const staleAt = offboardedAt + WAPI_OFFBOARDED_STALE_MS;
    if (now < patienceEnds) {
      return {
        disconnection: buildDisconnection(input, { case: '8a' }),
        recheckAt: patienceEnds,
      };
    }
    if (now < staleAt) {
      return {
        disconnection: buildDisconnection(input, { case: '8b' }),
        recheckAt: staleAt,
      };
    }
    return {
      disconnection: buildDisconnection(input, { case: '8d' }),
      recheckAt: null,
    };
  }

  // Ajuste 6 (19-oct): the reconnection is news only once the account is
  // CONNECTED, never while Meta is still activating it.
  const outcome = input.reconnectionOutcome;
  const reconnectedAt = toEpochMs(outcome?.at);
  const reconnectionCase =
    input.state === 'CONNECTED' && outcome
      ? resolveReconnectionCase(outcome)
      : null;
  if (reconnectionCase && reconnectedAt !== null) {
    const newsEnds = reconnectedAt + WAPI_RECONNECTION_NEWS_MS;
    if (now < newsEnds) {
      return {
        disconnection: buildDisconnection(input, {
          case: reconnectionCase,
          occurrence: outcome?.at ?? null,
        }),
        recheckAt: newsEnds,
      };
    }
  }

  return { disconnection: null, recheckAt: null };
}

/**
 * Ajuste 2 (19-oct) — two reconnection messages, picked by the campaign
 * counts and not by `same_waba` alone:
 * - campaigns resumed (> 0): "conectado de nuevo y tus campañas se
 *   reanudaron", whichever account it is (a manual reconnection with another
 *   account moves the campaigns along);
 * - none resumed and another account: "otra cuenta" (WBS writes the outcome
 *   on every account change, campaigns or not, INT-1676);
 * - none resumed and the same account: nothing to announce.
 */
export function resolveReconnectionCase(
  outcome: NonNullable<tWapiDisconnectionInput['reconnectionOutcome']>,
): tWapiDisconnectionCase | null {
  if ((outcome.resumed_campaigns ?? 0) > 0) return '8c_same';
  if (outcome.same_waba === false) return '8c_other';
  return null;
}

const LIVE_ROW_CASES: tWapiDisconnectionCase[] = [
  '8a',
  '8b',
  '8d',
  '8c_same',
  '8c_other',
];

/**
 * The notices a LIVE account raises (Meta moving the number, or the
 * reconnection that followed). They fire on their own: the row is not
 * DISCONNECTED, so `wapiDisconnected` is false for them.
 */
export function isLiveWapiNotice(
  disconnection: tWapiDisconnection | null,
): boolean {
  return disconnection !== null && LIVE_ROW_CASES.includes(disconnection.case);
}

/**
 * INT-1653 — what the menu dot and the Home badge say about WhatsApp API,
 * read off the live disconnection notice so the three surfaces can never
 * disagree: `disconnected` (red), `reconnecting` while Meta moves the number
 * (8a, 8b; blue), or null (no notice, or the number is back: 8c).
 */
export type tWapiConnectionSignal = 'disconnected' | 'reconnecting' | null;

export function resolveWapiConnectionSignal(
  alerts: { id: string; copy: { analytics?: Record<string, unknown> } }[],
): tWapiConnectionSignal {
  const notice = alerts.find((alert) => alert.id === 'wapi-disconnected');
  if (!notice) return null;
  const noticeCase = notice.copy.analytics?.disconnection_case;
  // 8d (a day out) is no longer "reconnecting": it reads as disconnected.
  if (noticeCase === '8a' || noticeCase === '8b') return 'reconnecting';
  if (noticeCase === '8c_same' || noticeCase === '8c_other') return null;
  return 'disconnected';
}
